/**
 * Dashboard — docs/08-frontend-and-ux.md §3.2.
 *
 * The doc's three cards ("what changed, what needs me, where am I in the tree")
 * need proposals (M2), photos (M4) and the tree renderer (M5). What M0 can answer
 * honestly is the third question's prerequisite — who you are, what you may do,
 * and where the council stands — so that is what this renders.
 */

import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { ClaimIdentity } from '../people/ClaimIdentity.js';
import { settingsApi } from '../../api/client.js';
import { useSession } from '../../app/session.js';
import styles from '../../ui/components.module.css';

export function HomePage(): JSX.Element {
  const { t } = useTranslation();
  const { user } = useSession();

  const settings = useQuery({
    queryKey: ['settings'],
    queryFn: () => settingsApi.get(),
    staleTime: 30_000,
  });

  const view = settings.data?.settings;

  return (
    <div style={{ display: 'grid', gap: 'var(--space-5)' }}>
      <h1>{t('home.greeting', { name: user?.display_name ?? '' })}</h1>

      {/* An account that is nobody in the tree can read but not propose; this is
          where they say who they are (docs/05 §11.4). Renders nothing once linked. */}
      <ClaimIdentity />

      {view?.bootstrap_mode && (
        <div className={styles.notice} role="status">
          <strong>{t('home.bootstrapTitle')}</strong>
          <p style={{ margin: 'var(--space-2) 0 0' }}>
            {t('home.bootstrapBody', { threshold: view.approval_threshold })}
          </p>
        </div>
      )}

      <section className={styles.card}>
        <h2>{t('home.yourAccount')}</h2>
        <dl
          style={{
            display: 'grid',
            gridTemplateColumns: 'auto 1fr',
            gap: 'var(--space-2) var(--space-5)',
          }}
        >
          <dt className={styles.muted}>{t('home.role')}</dt>
          <dd style={{ margin: 0 }}>{user?.role}</dd>

          <dt className={styles.muted}>{t('home.status')}</dt>
          <dd style={{ margin: 0 }}>{user?.status}</dd>

          <dt className={styles.muted}>{t('home.seat')}</dt>
          <dd style={{ margin: 0 }} className="tabular">
            {user?.seat ? `#${user.seat.seat_no}` : t('home.noSeat')}
          </dd>

          <dt className={styles.muted}>{t('home.canPropose')}</dt>
          <dd style={{ margin: 0 }}>{user?.can_propose ? t('home.yes') : t('home.no')}</dd>

          <dt className={styles.muted}>{t('home.linkedPerson')}</dt>
          <dd style={{ margin: 0 }}>{user?.person_id ?? t('home.notLinked')}</dd>
        </dl>
        {!user?.person_id && <p className={styles.muted}>{t('home.notLinkedHelp')}</p>}
      </section>

      {view && (
        <section className={styles.card}>
          <h2>{t('home.council')}</h2>
          <p className="tabular">
            {t('home.councilSeats', { active: view.active_seats, size: view.council_size })}
          </p>
          <p className={`${styles.muted} tabular`}>
            {t('home.threshold', { threshold: view.approval_threshold })}
          </p>
        </section>
      )}

      <p className={styles.muted}>{t('home.milestone')}</p>
    </div>
  );
}
